/**
 * Het profielmenu rechtsboven: wie je bent, bij welk bedrijf je zit, en de weg naar buiten
 * (US-002, US-003).
 *
 * Hiervoor was de avatar zélf de uitlogknop. Dat is de val die niemand verwacht: je klikt op je
 * profiel om te zien wie je bent en je staat buiten. Er stond een tekstballon bij, maar die zie je pas
 * nadat je hebt stilgehouden — en dat doe je niet vóór een klik.
 *
 * Twee handelingen horen hier bij elkaar, en dat is niet uit gemak. Wisselen van bedrijf gebeurde in
 * de praktijk dóór uit te loggen en opnieuw in te loggen: het bedrijfskeuzescherm was de enige plek
 * waar het kon, en dat scherm komt alleen bij het inloggen langs. Het zijn twee vormen van dezelfde
 * vraag — in welke omgeving zit ik en hoe kom ik in een andere — dus staan ze op dezelfde plek.
 */

import { ROLE_LABELS } from '@prijsmonitor/shared';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useSession } from '../session/SessionContext.js';
import styles from './ProfielMenu.module.css';

/** Bedrijfsafkorting voor een bedrijf zonder logo, bv. "NPI". Zelfde vorm als het keuzescherm. */
function afkorting(naam: string): string {
  const woorden = naam.split(/\s+/).filter(Boolean);
  if (woorden.length === 1) return (woorden[0] as string).slice(0, 3).toUpperCase();
  return woorden
    .slice(0, 3)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

export function ProfielMenu(): React.JSX.Element {
  const { session, activeCompany, role, chooseCompany, logout } = useSession();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const wrapper = useRef<HTMLDivElement>(null);
  const knop = useRef<HTMLButtonElement>(null);

  /*
   * Sluiten bij een klik ernaast en bij Escape.
   *
   * `pointerdown` en niet `click`: een klik op een knop elders in het scherm hoort dit menu te
   * sluiten én die knop te bedienen, en met `click` zou de eerste klik alleen het menu sluiten.
   */
  useEffect(() => {
    if (!open) return;

    function bijPointerdown(event: PointerEvent): void {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    }
    function bijToets(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // De aandacht terug naar de knop; anders staat de cursor nergens na het sluiten.
      knop.current?.focus();
    }

    document.addEventListener('pointerdown', bijPointerdown);
    document.addEventListener('keydown', bijToets);
    return () => {
      document.removeEventListener('pointerdown', bijPointerdown);
      document.removeEventListener('keydown', bijToets);
    };
  }, [open]);

  const bedrijven = session?.memberships ?? [];
  const naam = session?.user.displayName?.trim() || session?.user.email || 'Onbekende gebruiker';

  async function wissel(companyId: string): Promise<void> {
    if (companyId === activeCompany?.companyId) {
      setOpen(false);
      return;
    }
    setBezig(companyId);
    setFout(null);
    try {
      await chooseCompany(companyId);
      setOpen(false);
      /*
       * Naar het prijsoverzicht, net als na het inloggen. Blijven staan waar je stond kan niet: een
       * productdetail of een bronconfigurator hangt aan een id van het vórige bedrijf, en dat bestaat
       * in het nieuwe niet. Dan zou een wissel op een foutmelding uitkomen.
       */
      void navigate('/prijsoverzicht');
    } catch {
      setFout('Het bedrijf kon niet worden geopend. Probeer het opnieuw.');
    } finally {
      setBezig(null);
    }
  }

  return (
    <div className={styles.wrapper} ref={wrapper}>
      <button
        type="button"
        ref={knop}
        className={styles.avatar}
        aria-haspopup="true"
        aria-expanded={open}
        title={`${naam} — profiel, bedrijf wisselen en uitloggen`}
        onClick={() => setOpen((v) => !v)}
      >
        {session?.user.initials ?? '?'}
      </button>

      {open && (
        <div className={styles.paneel}>
          <div className={styles.kop}>
            <span className={styles.kopAvatar} aria-hidden="true">
              {session?.user.initials ?? '?'}
            </span>
            <span className={styles.kopTekst}>
              <span className={styles.kopNaam}>{naam}</span>
              {session?.user.displayName && (
                <span className={styles.kopEmail}>{session.user.email}</span>
              )}
              {activeCompany && (
                <span className={styles.kopRol}>
                  {activeCompany.companyName} · {role ? ROLE_LABELS[role] : 'onbekende rol'}
                </span>
              )}
            </span>
          </div>

          {bedrijven.length > 1 && (
            <>
              <div className={styles.scheiding} />
              <div className={styles.groepskop} id="profielmenu-bedrijven">
                Bedrijf wisselen
              </div>
              <div className={styles.bedrijven} aria-labelledby="profielmenu-bedrijven">
                {bedrijven.map((bedrijf) => {
                  const huidig = bedrijf.companyId === activeCompany?.companyId;
                  return (
                    <button
                      key={bedrijf.companyId}
                      type="button"
                      className={huidig ? styles.bedrijfHuidig : styles.bedrijf}
                      disabled={bezig !== null}
                      aria-current={huidig}
                      onClick={() => void wissel(bedrijf.companyId)}
                    >
                      {bedrijf.heeftLogo ? (
                        <img
                          className={styles.bedrijfLogo}
                          src={`/api/bedrijven/${bedrijf.companyId}/logo`}
                          alt=""
                        />
                      ) : (
                        <span className={styles.bedrijfAfkorting} aria-hidden="true">
                          {afkorting(bedrijf.companyName)}
                        </span>
                      )}
                      <span className={styles.bedrijfNaam}>{bedrijf.companyName}</span>
                      <span className={styles.bedrijfRol}>{ROLE_LABELS[bedrijf.role]}</span>
                      <span className={styles.bedrijfStatus} aria-hidden="true">
                        {bezig === bedrijf.companyId ? (
                          <span className="spinner" />
                        ) : huidig ? (
                          '✓'
                        ) : (
                          '→'
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              {fout && (
                <div className={styles.fout} role="alert">
                  {fout}
                </div>
              )}
            </>
          )}

          <div className={styles.scheiding} />
          <button type="button" className={styles.uitloggen} onClick={() => void logout()}>
            Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}
